import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, Users, Plus, History, CheckCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { getAvatarUrl } from '../utils/avatarUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MatchesPage = () => {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [historyMatches, setHistoryMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, open, my, history

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      const [matchesRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/matches`, { withCredentials: true }),
        axios.get(`${API_URL}/api/matches/history`, { withCredentials: true })
      ]);
      setMatches(matchesRes.data);
      setHistoryMatches(historyRes.data);
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMatches = filter === 'history' 
    ? historyMatches 
    : matches.filter(match => {
        if (filter === 'open') return match.status === 'open';
        if (filter === 'my') return match.players.some(p => p.user_id === user?.user_id);
        return match.status !== 'cancelled' && match.status !== 'completed';
      });

  const formatMatchDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'EEEE d MMMM', { locale: it });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-slate-100 rounded-3xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fadeIn" data-testid="matches-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-slate-900">
          Partite
        </h1>
        <Link
          to="/matches/new"
          data-testid="create-match-btn"
          className="inline-flex items-center gap-2 bg-sky-400 text-white rounded-full px-5 py-2.5 font-medium hover:bg-sky-500 transition-all hover:-translate-y-0.5 hover:shadow-lg"
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Nuova Partita</span>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { key: 'all', label: 'Tutte' },
          { key: 'open', label: 'Disponibili' },
          { key: 'my', label: 'Le Mie' },
          { key: 'history', label: 'Storico', icon: History }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`filter-${f.key}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              filter === f.key
                ? f.key === 'history' ? 'bg-green-500 text-white' : 'bg-sky-400 text-white'
                : 'bg-white text-slate-600 border border-slate-200 hover:border-sky-200'
            }`}
          >
            {f.icon && <f.icon className="w-4 h-4" />}
            {f.label}
          </button>
        ))}
      </div>

      {/* Info prenotazione campo */}
      <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200 mb-6" data-testid="court-booking-info">
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mt-0.5">
            <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-sm text-amber-700">
            Per ogni partita creata, ricorda di prenotare il campo presso la struttura Ananda Sporting Club tramite l'app <strong>Prenotazione Campi</strong>.
          </p>
        </div>
        <div className="flex gap-3 mt-3 ml-11">
          <a
            href="https://apps.apple.com/it/app/ananda-sporting-club/id6502181699"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="app-store-link-matches"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            App Store
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=it.anytimes.anandasportingclub.app"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="play-store-link-matches"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.24-.84-.76-.84-1.35zm13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27zm.91-.91L19.59 12l-1.87-2.21-2.27 2.27 2.27 2.15zM6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z"/></svg>
            Google Play
          </a>
        </div>
      </div>

      {/* Matches List */}
      {filteredMatches.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 border border-slate-100 text-center">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {filter === 'history' ? (
              <History className="w-10 h-10 text-slate-400" />
            ) : (
              <Calendar className="w-10 h-10 text-slate-400" />
            )}
          </div>
          <h3 className="font-outfit text-xl font-medium text-slate-900 mb-2">
            {filter === 'history' 
              ? 'Nessuna partita completata'
              : 'Nessuna partita trovata'}
          </h3>
          <p className="text-slate-500 mb-6">
            {filter === 'my' 
              ? 'Non sei iscritto a nessuna partita'
              : filter === 'history'
              ? 'Le partite completate appariranno qui'
              : 'Non ci sono partite disponibili al momento'}
          </p>
          {filter !== 'history' && (
            <Link
              to="/matches/new"
              className="inline-flex items-center gap-2 bg-sky-400 text-white rounded-full px-6 py-3 font-medium hover:bg-sky-500 transition-all"
            >
              <Plus className="w-5 h-5" />
              Crea la prima partita
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredMatches.map((match, index) => (
            <Link
              key={match.match_id}
              to={`/matches/${match.match_id}`}
              data-testid={`match-card-${index}`}
              className={`bg-white rounded-3xl p-6 border transition-all hover:-translate-y-1 hover:shadow-lg ${
                match.status === 'completed'
                  ? 'border-green-200 hover:border-green-300'
                  : match.status === 'open'
                  ? 'border-green-200 hover:border-green-300'
                  : match.status === 'full'
                  ? 'border-sky-200 hover:border-sky-300'
                  : 'border-slate-100 hover:border-slate-200'
              }`}
            >
              {/* Status badge */}
              <div className="flex items-center justify-between mb-4">
                <span className={`flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full ${
                  match.status === 'completed'
                    ? 'bg-green-100 text-green-600'
                    : match.status === 'open'
                    ? 'bg-green-100 text-green-600'
                    : match.status === 'full'
                    ? 'bg-sky-100 text-sky-600'
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {match.status === 'completed' && <CheckCircle className="w-3 h-3" />}
                  {match.status === 'open' ? 'Aperta' : match.status === 'full' ? 'Completa' : match.status === 'completed' ? 'Giocata' : match.status}
                </span>
                {match.players.some(p => p.user_id === user?.user_id) && match.status !== 'completed' && (
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-600">
                    Iscritto
                  </span>
                )}
              </div>

              {/* Date and time */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-sky-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 capitalize">
                    {formatMatchDate(match.date)}
                  </p>
                  <p className="font-semibold text-slate-900 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {match.time}
                  </p>
                </div>
              </div>

              {/* Creator */}
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-slate-100">
                <div className="w-8 h-8 bg-sky-400 rounded-full flex items-center justify-center text-white text-xs font-medium">
                  {match.creator_name?.charAt(0)}
                </div>
                <div>
                  <p className="text-xs text-slate-400">Organizzata da</p>
                  <p className="text-sm font-medium text-slate-700">{match.creator_name}</p>
                </div>
              </div>

              {/* Players */}
              <div className="flex items-center justify-between">
                <div className="flex -space-x-2">
                  {match.players.slice(0, 4).map((player, i) => (
                    <div
                      key={player.user_id}
                      className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-xs font-medium text-slate-600 border-2 border-white"
                      title={player.name}
                    >
                      {getAvatarUrl(player) ? (
                        <img src={getAvatarUrl(player)} alt={player.name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        player.name?.charAt(0)
                      )}
                    </div>
                  ))}
                  {match.players.length > 4 && (
                    <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-xs font-medium text-slate-500 border-2 border-white">
                      +{match.players.length - 4}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 text-slate-500">
                  <Users className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {match.players.length}/{match.max_players}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MatchesPage;
